import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { firebaseService } from '../../services/firebaseService';
import { useNotification } from '../../hooks/useNotification';
import { Building2, Save, ArrowLeft, MapPin, FileText, Hash } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Empresa } from '../../services/firebaseService';
import { validateCNPJ, formatCNPJ } from '../../utils/cnpjValidator';

const DadosEmpresa: React.FC = () => {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const { showSuccess, showError, NotificationComponent } = useNotification();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<Partial<Empresa>>({
    nomeFantasia: '',
    razaoSocial: '',
    cnpj: '',
    inscricaoEstadual: '',
    logradouro: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    cep: '',
    googlePlaceId: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    loadEmpresa();
  }, [userProfile?.empresaCnpj]);

  const loadEmpresa = async () => {
    if (!userProfile?.empresaCnpj) return;

    try {
      setLoading(true);
      const empresa = await firebaseService.getEmpresa(userProfile.empresaCnpj);
      
      if (empresa) {
        setFormData({
          nomeFantasia: empresa.nomeFantasia || '',
          razaoSocial: empresa.razaoSocial || '',
          cnpj: empresa.cnpj || '',
          inscricaoEstadual: empresa.inscricaoEstadual || '',
          logradouro: empresa.logradouro || '',
          numero: empresa.numero || '',
          complemento: empresa.complemento || '',
          bairro: empresa.bairro || '',
          cidade: empresa.cidade || '',
          cep: empresa.cep || '',
          googlePlaceId: empresa.googlePlaceId || ''
        });
      }
    } catch (error: any) {
      console.error('Erro ao carregar empresa:', error);
      showError('Erro ao carregar', 'Não foi possível carregar os dados da empresa.');
    } finally {
      setLoading(false);
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.nomeFantasia?.trim()) {
      newErrors.nomeFantasia = 'Nome fantasia é obrigatório';
    }

    if (!formData.razaoSocial?.trim()) {
      newErrors.razaoSocial = 'Razão social é obrigatória';
    }

    if (!formData.cnpj?.trim()) {
      newErrors.cnpj = 'CNPJ é obrigatório';
    } else if (!validateCNPJ(formData.cnpj)) {
      newErrors.cnpj = 'CNPJ inválido. Verifique os dígitos verificadores.';
    }

    if (!formData.cep?.trim()) {
      newErrors.cep = 'CEP é obrigatório';
    } else if (formData.cep.replace(/\D/g, '').length !== 8) {
      newErrors.cep = 'CEP deve ter 8 dígitos';
    }

    if (!formData.logradouro?.trim()) {
      newErrors.logradouro = 'Logradouro é obrigatório';
    }

    if (!formData.numero?.trim()) {
      newErrors.numero = 'Número é obrigatório';
    }

    if (!formData.bairro?.trim()) {
      newErrors.bairro = 'Bairro é obrigatório';
    }

    if (!formData.cidade?.trim()) {
      newErrors.cidade = 'Cidade é obrigatória';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof Empresa, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleCNPJChange = (value: string) => {
    const formatted = formatCNPJ(value);
    handleInputChange('cnpj', formatted);
  };

  const maskCEP = (value: string): string => {
    return value
      .replace(/\D/g, '')
      .replace(/^(\d{5})(\d)/, '$1-$2')
      .substring(0, 9);
  };

  const handleSave = async () => {
    if (!userProfile?.empresaCnpj) return;
    if (!validate()) return;

    try {
      setSaving(true);
      await firebaseService.updateEmpresa(userProfile.empresaCnpj, formData);
      showSuccess('Dados atualizados', 'Os dados da empresa foram atualizados com sucesso!');
    } catch (error: any) {
      console.error('Erro ao salvar empresa:', error);
      showError('Erro ao salvar', error.message || 'Não foi possível salvar os dados da empresa.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <NotificationComponent />
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/configuracoes')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Dados da Empresa</h1>
            <p className="text-gray-600">Configure as informações básicas da sua empresa</p>
          </div>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Save className="h-4 w-4" />
          {saving ? 'Salvando...' : 'Salvar'}
        </button>
      </div>

      {/* Form */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 space-y-6">
        {/* Informações Básicas */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <FileText className="h-5 w-5 text-primary-600" />
            <h2 className="text-lg font-semibold text-gray-900">Informações Básicas</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nome Fantasia <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.nomeFantasia || ''}
                onChange={(e) => handleInputChange('nomeFantasia', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  errors.nomeFantasia ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Ex: Academia de Natação XYZ"
              />
              {errors.nomeFantasia && <p className="text-red-500 text-xs mt-1">{errors.nomeFantasia}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Razão Social <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.razaoSocial || ''}
                onChange={(e) => handleInputChange('razaoSocial', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  errors.razaoSocial ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Ex: XYZ Academia de Natação Ltda"
              />
              {errors.razaoSocial && <p className="text-red-500 text-xs mt-1">{errors.razaoSocial}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                CNPJ <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.cnpj || ''}
                onChange={(e) => handleCNPJChange(e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  errors.cnpj ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="00.000.000/0000-00"
                maxLength={18}
              />
              {errors.cnpj && <p className="text-red-500 text-xs mt-1">{errors.cnpj}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Inscrição Estadual
              </label>
              <input
                type="text"
                value={formData.inscricaoEstadual || ''}
                onChange={(e) => handleInputChange('inscricaoEstadual', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="000.000.000.000"
              />
            </div>
          </div>
        </div>

        {/* Endereço */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <MapPin className="h-5 w-5 text-primary-600" />
            <h2 className="text-lg font-semibold text-gray-900">Endereço</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Logradouro <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.logradouro || ''}
                onChange={(e) => handleInputChange('logradouro', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  errors.logradouro ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Ex: Rua das Flores"
              />
              {errors.logradouro && <p className="text-red-500 text-xs mt-1">{errors.logradouro}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Número <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.numero || ''}
                onChange={(e) => handleInputChange('numero', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  errors.numero ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="123"
              />
              {errors.numero && <p className="text-red-500 text-xs mt-1">{errors.numero}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Complemento
              </label>
              <input
                type="text"
                value={formData.complemento || ''}
                onChange={(e) => handleInputChange('complemento', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Sala 101, Bloco A"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Bairro <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.bairro || ''}
                onChange={(e) => handleInputChange('bairro', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  errors.bairro ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Centro"
              />
              {errors.bairro && <p className="text-red-500 text-xs mt-1">{errors.bairro}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Cidade <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.cidade || ''}
                onChange={(e) => handleInputChange('cidade', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  errors.cidade ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="São Paulo"
              />
              {errors.cidade && <p className="text-red-500 text-xs mt-1">{errors.cidade}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                CEP <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.cep || ''}
                onChange={(e) => handleInputChange('cep', maskCEP(e.target.value))}
                className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                  errors.cep ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="00000-000"
                maxLength={9}
              />
              {errors.cep && <p className="text-red-500 text-xs mt-1">{errors.cep}</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DadosEmpresa;

